import React, { useEffect, useState } from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import { Broker } from 'models';
import PhoneIphoneIcon from '@material-ui/icons/PhoneIphone';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import { images } from 'assets/images';
import { COLOR } from 'config/constants';
import { getPhoneNumberWithFormatting } from 'utils';
import PortalHeader from './PortalHeader';
import SocialMediaIcons from './SocialMediaIcons';

interface Props {
  broker: Broker;
  slogan: string;
  bio: string;
  profile_picture_url: string;
  banner_picture_url: string;
  logo_picture_url?: string;
  portal_infos: any;
  sections?: any;
  favorite_listings: any;
  is_white_text?: boolean;
}

function PageHeader(props: Props) {
  const {
    broker,
    slogan,
    bio,
    profile_picture_url,
    banner_picture_url,
    portal_infos,
    logo_picture_url,
    sections,
    favorite_listings,
    is_white_text,
  } = props;

  const useStyles = makeStyles({
    headerContent: {
      height: 'auto',
    },
    header: {
      display: 'flex',
      position: 'relative',
      height: 600,
      paddingTop: 60,
      overflow: 'hidden',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      '@media (max-width: 800px)': {
        height: 450,
      },
    },
    stickyHeader: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: 'auto',
      backgroundColor: COLOR.WHITE,
      zIndex: 999,
    },
    brokerImageAndBio: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    brokerInfos: {
      position: 'relative',
      display: 'flex',
      top: -128,
      height: 'auto',
      flexDirection: 'column',
      alignItems: 'center',
      '@media (max-width: 800px)': {
        top: -96,
      },
    },
    profilePicture: {
      borderRadius: '50%',
      width: 256,
      height: 256,
      objectFit: 'cover',
      marginBottom: 20,
      '@media (max-width: 800px)': {
        width: 192,
        height: 192,
      },
    },
    brokerContactInfos: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: 20,
      width: '100%',
      '@media (max-width: 1340px)': {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        width: 'initial',
        marginTop: 0,
      },
    },
    brokerContactInfosItem: {
      display: 'flex',
      marginRight: 10,
      alignItems: 'center',
      '@media (max-width: 1340px)': {
        marginTop: 20,
        minWidth: 300,
      },
    },
    socialMediaIcon: {
      marginRight: 10,
      width: 30,
      height: 30,
      color: COLOR.ULTRA_DARK_BLUE,
    },
    brokerBio: {
      marginTop: 10,
      maxWidth: '50%',
      minWidth: 300,
      textAlign: 'justify',
      borderRadius: 5,
      backgroundColor: '#f5f5f5',
      whiteSpace: 'pre-line',
      padding: 10,
    },
    bannerImage: {
      width: '100%',
      height: 600,
      left: 0,
      position: 'absolute',
      objectFit: 'cover',
      '@media (max-width: 800px)': {
        height: 420,
      },
    },
    socialMediaContent: {
      marginTop: 10,
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'row',
    },
  });

  const styles = useStyles();

  return (
    <Box className={styles.headerContent}>
      <Box className={styles.stickyHeader}>
        <PortalHeader
          displaySocialMedia
          logo_picture_url={logo_picture_url}
          portal_infos={portal_infos}
          broker={broker}
          sections={sections}
          favorite_listings={favorite_listings}
          is_white_text={is_white_text}
        />
      </Box>
      <Box className={styles.header}>
        <img
          src={banner_picture_url || images.default_portal_banner}
          alt='banner'
          className={styles.bannerImage}
        />
      </Box>
      <Box className={styles.brokerInfos}>
        <Box className={styles.brokerImageAndBio}>
          <img
            className={styles.profilePicture}
            style={{ backgroundColor: profile_picture_url ? COLOR.WHITE : null }}
            src={profile_picture_url}
            alt='Avatar'
          />
          <Typography style={{ fontFamily: 'FordAntenna-SemiBold' }} variant='h5'>
            {portal_infos && portal_infos.display_name
              ? portal_infos.display_name
              : broker.fullName}
          </Typography>
        </Box>
        <Box className={styles.brokerContactInfos}>
          {portal_infos.email && portal_infos.email !== null && (
            <Box className={styles.brokerContactInfosItem}>
              <EmailOutlinedIcon className={styles.socialMediaIcon} />
              <Typography
                onClick={() => {
                  window.location.href = `mailto:${portal_infos.email}`;
                }}
                style={{ cursor: 'pointer', fontSize: 16, fontWeight: 700 }}
              >
                {portal_infos.email}
              </Typography>
            </Box>
          )}
          <Box className={styles.brokerContactInfosItem}>
            {portal_infos.phone_number || broker.centrisPhoneNumber ? (
              <>
                <PhoneIphoneIcon className={styles.socialMediaIcon} />
                <Typography style={{ fontSize: 16, fontWeight: 700 }}>
                  {getPhoneNumberWithFormatting(
                    portal_infos.phone_number || broker.centrisPhoneNumber,
                  )}
                </Typography>
              </>
            ) : null}
          </Box>
        </Box>
        <Box className={styles.socialMediaContent}>
          <SocialMediaIcons portal_infos={portal_infos} broker={broker} />
        </Box>
        {bio ? (
          <Box className={styles.brokerBio}>
            <Typography variant='body2'>{bio}</Typography>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default PageHeader;
